import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Card, Col, Empty, Row, Select, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, EyeOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Storage } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getClasses } from 'app/entities/classe/classe.reducer';
import { getCycles } from 'app/entities/cycle/cycle.reducer';
import { getAnneeScolaire } from 'app/entities/annee-scolaire/annee-scolaire.reducer';
import { getPeriodes } from 'app/entities/periode-evaluation/periode-evaluation.reducer';
import { StatutPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';
import { BulletinPreviewModal } from './BulletinPreviewModal';
import { NoteDetailModal } from './NoteDetailModal';
import { BulletinRow } from './bulletins.types';
import { format2, getPeriodeSortRank, usesCoefficients, KORI_ORANGE } from './bulletins.utils';
import { clearBulletinsState, downloadBulletinPdf, getBulletins } from './note.reducer';

const { Title, Text } = Typography;

export const Bulletins = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string }>) => {
  const dispatch = useAppDispatch();

  const classes = useAppSelector(state => state.classe.entities);
  const cycles = useAppSelector(state => state.cycle.entities);
  const periodesByCycle = useAppSelector(state => state.periodeEvaluation.entitiesByCycle);
  const { anneeScolaireId } = props.match.params;
  const bulletins = useAppSelector(state => state.note.bulletins);
  const loading = useAppSelector(state => state.note.bulletinsLoading);

  const [cycleId, setCycleId] = useState<string | null>(null);
  const [classeId, setClasseId] = useState<string | null>(null);
  const [periodeId, setPeriodeId] = useState<string | null>(null);
  const [selectedInscriptionId, setSelectedInscriptionId] = useState<string | null>(null);
  const [emptyMessage, setEmptyMessage] = useState("Sélectionnez d'abord un cycle");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [detailBulletin, setDetailBulletin] = useState<BulletinRow | null>(null);
  const [detailPeriodeId, setDetailPeriodeId] = useState<string | null>(null);

  useEffect(() => {
    const storedPageSize = Storage.local.get('bulletins.pageSize');
    if (storedPageSize) {
      setPageSize(Number(storedPageSize));
    }

    dispatch(getClasses(props.match.params.ecoleid));
    dispatch(getCycles(props.match.params.ecoleid));
    dispatch(getAnneeScolaire(anneeScolaireId));
  }, []);

  useEffect(() => {
    setSelectedInscriptionId(null);
    setPage(1);
    dispatch(clearBulletinsState());
  }, [cycleId, classeId]);

  useEffect(() => {
    if (cycleId && anneeScolaireId) {
      dispatch(
        getPeriodes({
          ecoleId: props.match.params.ecoleid,
          anneeScolaireId,
          cycleId,
        })
      );
    }
  }, [cycleId, anneeScolaireId]);

  useEffect(() => {
    setSelectedInscriptionId(null);
    setPage(1);
  }, [periodeId]);

  const availableClasses = useMemo(() => (cycleId ? classes.filter(classe => classe.cycleId === cycleId) : []), [classes, cycleId]);

  const selectedClasse = useMemo(() => availableClasses.find(classe => classe.id === classeId) || null, [availableClasses, classeId]);

  const selectedCycle = useMemo(() => cycles.find(cycle => cycle.id === cycleId) || null, [cycles, cycleId]);

  const isSecondaryBulletin = usesCoefficients(selectedCycle?.scoringMode);

  const sortedPeriodes = useMemo(
    () =>
      [...(cycleId ? periodesByCycle[cycleId] || [] : [])]
        .filter(periode => periode.statut === StatutPeriodeEvaluation.OUVERTE || periode.statut === StatutPeriodeEvaluation.FERMEE)
        .sort((a, b) => getPeriodeSortRank(b) - getPeriodeSortRank(a)),
    [cycleId, periodesByCycle]
  );

  const isPeriodeAvailableForContext = useMemo(() => {
    if (!cycleId || !periodeId) {
      return false;
    }
    return sortedPeriodes.some(periode => periode.id === periodeId);
  }, [cycleId, periodeId, sortedPeriodes]);

  const handleCycleChange = (value?: string) => {
    const nextCycleId = value || null;
    setCycleId(nextCycleId);
    setClasseId(null);
    setPeriodeId(null);
  };

  const handlePeriodeChange = (value?: string) => {
    const nextPeriodeId = value || null;
    setPeriodeId(nextPeriodeId);
  };

  useEffect(() => {
    if (!cycleId) {
      if (classeId) {
        setClasseId(null);
      }
      return;
    }

    if (classeId && !availableClasses.some(classe => classe.id === classeId)) {
      setClasseId(null);
    }
  }, [availableClasses, classeId, cycleId]);

  useEffect(() => {
    if (periodeId && !sortedPeriodes.some(periode => periode.id === periodeId)) {
      setPeriodeId(null);
    }
  }, [periodeId, sortedPeriodes]);

  useEffect(() => {
    const load = async () => {
      if (!cycleId) {
        dispatch(clearBulletinsState());
        setEmptyMessage("Sélectionnez d'abord un cycle");
        return;
      }

      if (!periodeId) {
        dispatch(clearBulletinsState());
        setEmptyMessage("Sélectionnez une période d'évaluation");
        return;
      }

      if (!isPeriodeAvailableForContext) {
        dispatch(clearBulletinsState());
        setEmptyMessage('Chargement des périodes...');
        return;
      }

      try {
        const rows = await dispatch(getBulletins({ periodeId, classeId })).unwrap();
        if (!rows.length) {
          setEmptyMessage('Aucun bulletin trouvé pour cette période');
          return;
        }

        setEmptyMessage('Aucune donnée de bulletin disponible');
        setSelectedInscriptionId(current =>
          current && rows.some(row => row.inscriptionId === current) ? current : rows[0]?.inscriptionId || null
        );
      } catch {
        setEmptyMessage('Erreur lors du chargement des bulletins');
        message.error('Impossible de charger les bulletins');
      }
    };

    load();
  }, [cycleId, classeId, isPeriodeAvailableForContext, periodeId]);

  const selectedBulletin = useMemo(
    () => bulletins.find(row => row.inscriptionId === selectedInscriptionId) || null,
    [bulletins, selectedInscriptionId]
  );

  const selectedPeriode = useMemo(() => sortedPeriodes.find(periode => periode.id === periodeId) || null, [periodeId, sortedPeriodes]);

  const isPeriodeClosed = selectedPeriode?.statut === StatutPeriodeEvaluation.FERMEE;

  const printBulletin = async (bulletin: BulletinRow) => {
    if (!periodeId) return;
    if (!isPeriodeClosed) {
      message.warning('La génération est autorisée uniquement si la période est clôturée');
      return;
    }

    try {
      const result = await dispatch(
        downloadBulletinPdf({
          url: '/api/bulletins/eleve/pdf',
          fileName: `bulletin-${bulletin.matricule || bulletin.inscriptionId}.pdf`,
          params: {
            inscriptionId: bulletin.inscriptionId,
            periodeId,
          },
        })
      ).unwrap();
      if (result?.downloaded) {
        message.success('Bulletin téléchargé');
        return;
      }
      if (result?.opened) {
        message.success('Bulletin DRAFT ouvert dans un nouvel onglet');
        return;
      }

      message.warning("Le navigateur a bloqué l'ouverture du bulletin");
    } catch {
      message.error("Impossible d'ouvrir ou de télécharger le bulletin");
    }
  };

  const printClasseBulletins = async () => {
    if (!classeId || !periodeId) return;
    if (!isPeriodeClosed) {
      message.warning('La génération est autorisée uniquement si la période est clôturée');
      return;
    }

    try {
      const result = await dispatch(
        downloadBulletinPdf({
          url: '/api/bulletins/classe/pdf',
          fileName: `bulletins-classe-${selectedClasse?.nom || classeId}.pdf`,
          params: { classeId, periodeId },
        })
      ).unwrap();
      if (result?.downloaded) {
        message.success('Bulletins archivés de la classe téléchargés');
        return;
      }
      if (result?.opened) {
        message.success('Bulletins DRAFT de la classe ouverts dans un nouvel onglet');
        return;
      }

      message.warning("Le navigateur a bloqué l'ouverture des bulletins");
    } catch {
      message.error('Impossible de récupérer les bulletins de la classe');
    }
  };

  const openPreviewModal = (bulletin: BulletinRow) => {
    setSelectedInscriptionId(bulletin.inscriptionId);
    setPreviewVisible(true);
  };

  const openDetailModal = (bulletin: BulletinRow) => {
    setDetailBulletin(bulletin);
    setDetailPeriodeId(periodeId);
  };

  const bulletinColumns: ColumnsType<BulletinRow> = [
    ...(!classeId ? ([{ title: 'Classe', dataIndex: 'classeNom', key: 'classeNom' }] as ColumnsType<BulletinRow>) : []),
    { title: 'Matricule', dataIndex: 'matricule', key: 'matricule' },
    { title: 'Prénom', dataIndex: 'prenom', key: 'prenom' },
    { title: 'Nom', dataIndex: 'nom', key: 'nom' },
    {
      title: 'Moyenne générale',
      dataIndex: 'moyenneGenerale',
      key: 'moyenneGenerale',
      render(value: number | null, record) {
        if (value === null) return '-';
        const scale = record.noteScale || 20;
        const passMark = scale / 2;
        const color = value >= passMark ? '#2e7d32' : '#c62828';
        return <span style={{ color, fontWeight: 700 }}>{`${format2(value)}/${scale}`}</span>;
      },
    },
    {
      title: 'Rang',
      dataIndex: 'rang',
      key: 'rang',
      render(value: number | null) {
        return value ? `${value}/${bulletins.length}` : '-';
      },
    },
    { title: 'Mention', dataIndex: 'mention', key: 'mention' },
    {
      title: 'Actions',
      key: 'actions',
      render(_, record) {
        return (
          <Space>
            <Button type="text" icon={<EyeOutlined />} onClick={() => openPreviewModal(record)} />
            <Button type="text" icon={<UnorderedListOutlined />} onClick={() => openDetailModal(record)} title="Détails des notes" />
          </Space>
        );
      },
    },
  ];

  return (
    <Card style={{ margin: 24 }}>
      <Title level={2}>Relevé de notes / Bulletins</Title>

      <div style={{ marginTop: 4, marginBottom: 4, padding: 12, border: '1px solid #f4e0c7', borderRadius: 10, background: '#fffaf3' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Cycle
            </Text>
            <Select
              placeholder="Sélectionner un cycle"
              style={{ width: '100%' }}
              value={cycleId || undefined}
              onChange={handleCycleChange}
              allowClear
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {cycles.map(cycle => (
                <Select.Option key={cycle.id} value={cycle.id}>
                  {cycle.nom || '-'}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={6}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Période
            </Text>
            <Select
              placeholder={cycleId ? 'Sélectionner une période' : "Choisissez d'abord un cycle"}
              style={{ width: '100%' }}
              value={periodeId || undefined}
              onChange={handlePeriodeChange}
              allowClear
              size="large"
              showSearch
              optionFilterProp="children"
              disabled={!cycleId}
            >
              {sortedPeriodes.map(periode => (
                <Select.Option key={periode.id} value={periode.id}>
                  {`${periode.nom || '-'}`}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={6}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Classe
            </Text>
            <Select
              placeholder={cycleId ? 'Toutes les classes du cycle' : "Choisissez d'abord un cycle"}
              style={{ width: '100%' }}
              value={classeId || undefined}
              onChange={value => setClasseId(value || null)}
              allowClear
              size="large"
              showSearch
              optionFilterProp="children"
              disabled={!cycleId}
            >
              {availableClasses.map(classe => (
                <Select.Option key={classe.id} value={classe.id}>
                  {classe.nom}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                style={{ backgroundColor: KORI_ORANGE, borderColor: KORI_ORANGE }}
                disabled={!classeId || !periodeId || !isPeriodeClosed || bulletins.length === 0}
                onClick={printClasseBulletins}
              >
                Générer les bulletins
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ marginTop: 16 }}>
        {!cycleId ? (
          <Empty description="Sélectionnez d'abord un cycle" />
        ) : !periodeId ? (
          <Empty description="Sélectionnez une période pour afficher les bulletins" />
        ) : bulletins.length === 0 ? (
          <Empty description={emptyMessage} />
        ) : (
          <Table<BulletinRow>
            rowKey="inscriptionId"
            loading={loading}
            columns={bulletinColumns}
            dataSource={bulletins}
            pagination={{
              current: page,
              pageSize,
              total: bulletins.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange(nextPage, nextSize) {
                setPage(nextPage);
                if (nextSize && nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                  Storage.local.set('bulletins.pageSize', nextSize);
                }
              },
            }}
          />
        )}
      </div>

      <BulletinPreviewModal
        visible={previewVisible}
        selectedBulletin={selectedBulletin}
        bulletinsCount={bulletins.length}
        isPeriodeClosed={isPeriodeClosed}
        isSecondaryBulletin={isSecondaryBulletin}
        onClose={() => setPreviewVisible(false)}
        onGeneratePdf={printBulletin}
      />
      <NoteDetailModal
        visible={Boolean(detailBulletin)}
        isSecondaryBulletin={isSecondaryBulletin}
        bulletinMatieres={detailBulletin?.matieres || []}
        inscriptionId={detailBulletin?.inscriptionId || null}
        periodeId={detailPeriodeId}
        classeNom={detailBulletin?.classeNom || ''}
        periodeName={selectedPeriode?.nom || ''}
        nom={detailBulletin?.nom || ''}
        prenom={detailBulletin?.prenom || ''}
        matricule={detailBulletin?.matricule || ''}
        matiereNames={
          detailBulletin
            ? detailBulletin.matieres.reduce<Record<string, string>>((acc, matiere) => {
                acc[matiere.matiereId] = matiere.matiereNom;
                return acc;
              }, {})
            : {}
        }
        onClose={() => setDetailBulletin(null)}
      />
    </Card>
  );
};

export default Bulletins;
